<template>
    <div class="product-listing-card">
        <div
            ref="cardElement"
            class="card card-sm border bg-white border-border transition md:min-h-[507px] overflow-hidden"
            :class="{
                'h-[470px] minimal-card': boxLayout == 'minimal',
                'h-[560px] standard-card': boxLayout == 'standard',
                'h-[542px] category-card': boxLayout == null,
                'hover:border-buy-focus hover:shadow-xl': data.stock > 0
            }"
        >
            <ProductListingSelect
                v-if="showSelection"
                :product="data"
                :anchor="cardElement"
                :dialog-id="selectionId"
                @close-selection="closeSelection"
            />

            <div class="card-body [--card-fs:0.875rem] gap-0 flex justify-between">
                <div
                    v-if="data.customFields?.brand != 'Z' && boxLayout == null"
                    class="absolute right-2 top-2"
                >
                    <ProductBrandBadge
                        :brands="props?.data?.customFields?.brand"
                        :alt="data?.customFields?.brand_name"
                        size="md"
                        class="ml-auto"
                    />
                </div>

                <div :class="{'h-full': boxLayout == null}">
                    <MiscLink
                        class=" w-full overflow-hidden rounded-lg flex items-center justify-center"
                        :class="boxLayout == null ? 'h-[250px]' : 'mb-8'"
                        :to="data.url"
                        :aria-label="data.name"
                    >
                        <MiscImage
                            v-if="mainImage"
                            :lazy-load="true"
                            :loading="true"
                            :src="mainImage?.url"
                            :alt="data.name"
                            fit="fit"
                            class="aspect-square object-contain"
                            width="345"
                            height="345"
                            :class="boxLayout == null ? 'h-full' : 'h-[240px] max-w-none'"
                        />
                    </MiscLink>

                    <div :class="{'pt-2': boxLayout == null}">
                        <MiscLink
                            :to="data.url"
                            :aria-label="data.name"
                        >
                            <div
                                class="card-title items-start mb-1"
                                :class="boxLayout == null ? 'text-xl leading-5' : 'text-lg m-h-[84px] pt-2'"
                                v-text="data.name"
                            />
                        </MiscLink>

                        <div class="flex flex-col">
                            <div>
                                <div v-if="boxLayout == null && productFlags.length > 0" class="flex gap-3 pt-2">
                                    <div v-for="(flag, i) in productFlags" :key="i" class="flex items-center gap-0.5">
                                        <MiscIcon :name="flag.icon" size="lg" />
                                        <div class="text-base">{{ flag.label }}</div>
                                    </div>
                                </div>
                                <div class="flex gap-1 text-xs leading-5 text-placeholder-focus">
                                    <div v-if="data.customFields?.country_of_origin">
                                        {{ t('product.card.origin') }}: {{ data.customFields?.country_of_origin }}
                                    </div>
                                    <div
                                        v-if="data.customFields?.country_of_origin && data.customFields?.illustration_exemplary"
                                        aria-hidden="true"
                                    >
                                        |
                                    </div>
                                    <div v-if="data.customFields?.illustration_exemplary">
                                        {{ t('product.card.illustrationExemplary') }}
                                    </div>
                                </div>
                            </div>

                            <div class="pt-2">
                                <div
                                    class="text-xs leading-4 font-semibold"
                                    :class="{
                                        'text-success': data.deliveryTimeCustomFields?.available_for_purchase === 'available_for_purchase_orderable',
                                        'text-sale': data.deliveryTimeCustomFields?.available_for_purchase === 'available_for_purchase_not_orderable',
                                        'text-sold-out': data.deliveryTimeCustomFields?.available_for_purchase === 'available_for_purchase_limited'
                                    }"
                                >
                                    <span v-if="data.deliveryTimeCustomFields?.availability_text"
                                    >{{ data.deliveryTimeCustomFields?.availability_text }}</span>
                                    <span v-if="data.deliveryTimeCustomFields?.availability_additional_text"
                                    >, {{ data.deliveryTimeCustomFields?.availability_additional_text }}</span>
                                </div>

                                <div
                                    v-if="data.customFields?.just_delivery_service"
                                    class="text-xs leading-4"
                                >
                                    {{ t('product.card.shippingService') }}
                                </div>
                            </div>
                        </div>

                        <div
                            v-if="boxLayout != 'minimal'"
                            class="card-text absolute h-10 "
                            :class="{
                                'bottom-[100px]': boxLayout == null || showAddToCart,
                                'bottom-[85px]': boxLayout != null && !showAddToCart
                            }"
                        >
                            <div
                                :class="{
                                    'text-sale': hasSpecialPrice || data.customFields?.savings_text != null,
                                    'text-link': boxLayout == null && (!hasSpecialPrice && data.customFields?.savings_text == null),
                                    'text-primary-content': boxLayout != null && !hasSpecialPrice
                                }"
                                class="text-lg leading-5 font-medium"
                            >
                                <div v-if="data.variantsFrom" class="text-base-700 text-lg font-medium">
                                    <span>{{ t('product.card.from') }}</span>

                                    {{ cheapestPrice }}*
                                </div>

                                <template v-else>
                                    <span v-if="data.priceRange">{{ t('product.card.from') }}</span>

                                    {{ regularPrice }}*
                                </template>

                                <span
                                    v-if="data.customFields?.variant_info_text"
                                    class="ps-1 text-placeholder-focus font-normal"
                                >
                                    {{ data.customFields?.variant_info_text }}
                                </span>
                            </div>

                            <span v-if="hasSpecialPrice" class="text-sale text-sm">
                                {{ t('product.card.save') }} {{ formatPrice(discountPrice) }}* = {{
                                    discountPercentage
                                }}%
                            </span>

                            <span
                                v-if="data.customFields?.savings_text != null"
                                class="text-sale text-sm"
                                v-html="data.customFields?.savings_text"
                            />
                        </div>
                    </div>
                </div>

                <ProductPriceUnit v-if="boxLayout != 'minimal'" :product="data" class="mt-1"/>
            </div>

            <ProductFlags :data="data" />

            <div class="card-actions w-full justify-center xl:justify-between">
                <MiscLink
                    v-if="boxLayout != null && !showAddToCart"
                    no-prefetch
                    :to="data.url"
                    :aria-label="`${t('product.card.details')} ${data.name}`"
                    class="btn btn-primary text-center w-full border-none rounded rounded-t-none focus-visible:outline-offset-[-4px] focus-visible:outline-current"
                >
                    {{ t('product.card.details') }}
                    <MiscIcon
                        :name="'chevron-right'"
                        :size="'lg'"
                    />
                </MiscLink>
                <MiscLink
                    v-else-if="data.definedSet === false "
                    no-prefetch
                    :to="data.url"
                    :aria-label="`${t('product.card.chooseSetProducts')} ${data.name}`"
                    class="btn btn-buy text-center font-semibold text-lg w-full h-16 border-none rounded-none focus-visible:outline-offset-[-4px] focus-visible:outline-current"
                >
                    {{ t('product.card.chooseSetProducts') }}
                    <MiscIcon
                        :name="'chevron-right'"
                        :size="'lg'"
                    />
                </MiscLink>

                <button
                    v-else
                    ref="addToCartTrigger"
                    type="button"
                    class="btn btn-buy text-center font-semibold text-lg w-full h-16 border-none rounded-none focus-visible:outline-offset-[-4px] focus-visible:outline-current"
                    :disabled="data.stock < 0 || data.deliveryTimeCustomFields?.available_for_purchase === 'available_for_purchase_not_orderable'"
                    :aria-label="`${t('product.card.addToCart')} ${data.name}`"
                    aria-haspopup="dialog"
                    :aria-expanded="showSelection"
                    :aria-controls="showSelection ? selectionId : undefined"
                    @click="showSelection = true"
                >
                    {{ t('product.card.addToCart') }}
                    <MiscIcon
                        :name="'cart-box-arrow-down'"
                        :size="'lg'"
                    />
                </button>
            </div>
        </div>

        <div
            v-if="boxLayout == null"
            class="-bottom-[52px] h-11 flex gap-1 items-center justify-center w-full"
        >
            <WishlistProductToggleButton
                :product="data"
                :text-position="'left'"
                :icon-size="'lg'"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import {computed, nextTick, ref, useId} from 'vue'
import {useI18n} from 'vue-i18n'
import type {HblProduct} from '@/utils/types'
import {useCurrency} from '#imports'

const {t} = useI18n()

const props = defineProps<{
    data: HblProduct,
    boxLayout?: 'minimal' | 'standard' | null,
    showAddToCart?: boolean,
}>()

const {formatPrice} = useCurrency()

const showSelection = ref(false)
const selectionId = useId()
const cardElement = ref<HTMLElement | null>(null)
const addToCartTrigger = ref<HTMLButtonElement | null>(null)

function closeSelection(restoreFocus: boolean) {
    showSelection.value = false
    if (restoreFocus) {
        nextTick(() => addToCartTrigger.value?.focus())
    }
}

const mainImage = computed(() => Array.isArray(props.data.media) ? props.data.media[0] : props.data.media)

const specialPrice = computed(() => props.data?.customFields?.special_price)
const hasSpecialPrice = computed(() => props.data?.price?.specialPrice != null)
const cheapestPrice = computed(() => specialPrice.value ?? formatPrice(props.data?.cheapestPrice?.regularPrice))
const regularPrice = computed(() => specialPrice.value ?? formatPrice(props.data.price?.regularPrice))
const discountPrice = computed(() => Math.abs(Number(props.data?.cheapestPrice?.discount ?? props.data?.price?.discount)))
const discountPercentage = computed(() => Math.round(Number(props.data?.cheapestPrice?.percentage ?? props.data?.price?.percentage)))

const productFlags = computed(() =>
    [
        props.data.customFields?.is_eco && { icon: 'eco', label: t('product.card.eco') },
        props.data.customFields?.is_fresh_product && { icon: 'fresh', label: t('product.card.fresh') },
        props.data.customFields?.is_vegan && { icon: 'aus_dinkel', label: t('product.card.vegan') },
    ].filter(Boolean)
)
</script>
